"use client";

import Link from "next/link";
import { useCallback, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import FaqAnswerView from "@/components/faq/FaqAnswer";
import Modal from "@/components/ui/Modal";
import type { FaqEntry } from "@/lib/faq";

type ProductHelpFaqSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  entries: FaqEntry[];
};

/**
 * Thin Help FAQ sheet — renders catalog entries only (no Q&A ownership).
 */
export default function ProductHelpFaqSheet({
  isOpen,
  onClose,
  entries,
}: ProductHelpFaqSheetProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = useCallback((index: number) => {
    setOpenIndex((current) => (current === index ? null : index));
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="FAQs" maxWidth="md">
      <div className="space-y-4">
        <p className="text-sm text-text-light">
          Quick answers for where you are. Open all FAQs for the full list.
        </p>
        <div className="space-y-2">
          {entries.map((faq, index) => {
            const isOpenItem = openIndex === index;
            return (
              <div
                key={faq.id}
                className="overflow-hidden rounded-xl border border-border"
              >
                <button
                  type="button"
                  onClick={() => toggle(index)}
                  aria-expanded={isOpenItem}
                  className="flex w-full items-center justify-between bg-surface px-3 py-3 text-left hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <span className="pr-2 text-sm font-medium text-foreground">
                    {faq.question}
                  </span>
                  <ChevronDownIcon
                    className={`h-5 w-5 shrink-0 text-text-light transition-transform ${
                      isOpenItem ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpenItem ? (
                  <div className="border-t border-border px-3 py-3 text-sm text-text-light">
                    <FaqAnswerView answer={faq.answer} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        <div className="pt-1 text-center">
          <Link
            href="/faq"
            onClick={onClose}
            className="text-sm font-medium text-primary hover:underline"
          >
            View all FAQs
          </Link>
        </div>
      </div>
    </Modal>
  );
}
